import React, { useState } from 'react';
import { DropZone as DropZoneAria } from 'react-aria-components';

import { Button, Group,Spinner, Text } from '@ekhodealer/ekho-common/rev';

import type { RevProps } from '@ekhodealer/ekho-common/rev';
import type { ReactNode} from 'react';

import './Dropzone.scss';

export interface DropzoneProps extends RevProps {
	isDisabled?: boolean;
	isLoading?: boolean;
	isMultiple?: boolean;
	supportedMimeTypes: 'IMAGE' | string[];
	placeholder?: {
		title?: ReactNode;
		description?: ReactNode;
	};
	onFilesDropped?: (
		files: File[],
		errors?: {
			error: Error;
			fileName: string;
			mimeType: string;
		}[],
	) => Promise<void>;
}

interface SingleDropzoneFileDisplayProps {
	file: File;
	onReplace: () => void;
	isDisabled?: boolean;
}

const singleDropzoneFileDisplay = ({
	file,
	onReplace,
	isDisabled,
}: SingleDropzoneFileDisplayProps) => {
	return (
		<div data-rev-component='dropzone-file-display'>
			<div className='dropzone-file-display--rev'>
				<Group itemsAlignY='center' itemsSpacing='16'>
					<div className='dropzone-file-info--rev'>
						<div className='dropzone-file-icon-wrapper--rev'>
							<svg
								width='20'
								height='20'
								viewBox='0 0 20 20'
								fill='none'
								xmlns='http://www.w3.org/2000/svg'
								className='dropzone-file-icon--rev'>
								<path
									d='M13.3333 5.83333V13.3333C13.3333 14.25 12.5833 15 11.6667 15H5.83333C4.91667 15 4.16667 14.25 4.16667 13.3333V4.16667C4.16667 3.25 4.91667 2.5 5.83333 2.5H10.4167L13.3333 5.83333Z'
									stroke='currentColor'
									strokeWidth='1.5'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
								<path
									d='M10.4167 2.5V6.66667H13.3333'
									stroke='currentColor'
									strokeWidth='1.5'
									strokeLinecap='round'
									strokeLinejoin='round'
								/>
							</svg>
						</div>
						<Text size='bodySmall' weight='regular' className='dropzone-file-name--rev'>
							{file.name}
						</Text>
					</div>
					<Button
						variant='secondary'
						size='medium'
						onPress={onReplace}
						isDisabled={isDisabled}>
						Replace file
					</Button>
				</Group>
			</div>
		</div>
	);
};

export function Dropzone(props: DropzoneProps) {
	const [internalLoading, setInternalLoading] = useState(false);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const isLoading = internalLoading || props.isLoading;

	const inputRef = React.useRef<HTMLInputElement>(null);

	const mimeTypes = Array.isArray(props.supportedMimeTypes)
		? props.supportedMimeTypes
		: props.supportedMimeTypes === 'IMAGE'
		? ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
		: [];

	const handleReplaceFile = () => {
		setSelectedFiles([]);
		inputRef.current?.click();
	};

	const handleFilesSelected = async (
		files: File[],
		rejected?: {
			error: Error;
			fileName: string;
			mimeType: string;
		}[],
	) => {
		setInternalLoading(true);

		try {
			await props.onFilesDropped?.(files, rejected);

			// Track selected files
			if (files.length > 0) {
				setSelectedFiles(files);
			}
		} finally {
			setInternalLoading(false);
		}
	};

	const hasSelectedFile = !props.isMultiple && selectedFiles.length > 0;

	return (
		<>
			{hasSelectedFile ? (
				singleDropzoneFileDisplay({
					file: selectedFiles[0],
					onReplace: handleReplaceFile,
					isDisabled: props.isDisabled,
				})
			) : (
				<DropZoneAria
					data-rev-component='dropzone'
					isDisabled={props.isDisabled}
					data-loading={isLoading}
					getDropOperation={(types) =>
						mimeTypes.some((t) => types.has(t)) ? 'copy' : 'cancel'
					}
					onDrop={async (event) => {
						setInternalLoading(true);
						const dropFiles = event.items.filter((i) => i.kind === 'file');

						const results = await Promise.allSettled(
							dropFiles.map(async (item) => {
								try {
									return await item.getFile();
								} catch (error) {
									throw {
										error: error as Error,
										fileName: item.name,
										mimeType: item.type,
									};
								}
							}),
						);

						const fulfilled = results
							.filter(
								(r): r is PromiseFulfilledResult<File> => r.status === 'fulfilled',
							)
							.map((r) => r.value);

						const rejected = results
							.filter((r): r is PromiseRejectedResult => r.status === 'rejected')
							.map(
								(r) =>
									r.reason as {
										error: Error;
										fileName: string;
										mimeType: string;
									},
							);

						await handleFilesSelected(fulfilled, rejected);
					}}>
					<div
						className='dropzone-container--rev'
						onClick={() => {
							if (props.isDisabled) return;
							inputRef.current?.click();
						}}>
						{isLoading ? (
							<div className='dropzone-loading-overlay--rev'>
								<div className='dropzone-loading-overlay-group--rev'>
									<Spinner size='small' />
								</div>
								<Text size='bodySmall' color='tertiary' align='center'>
									Uploading...
								</Text>
							</div>
						) : (
							<>
								{/* Hidden input for clicking dropzone */}
								<input
									ref={inputRef}
									type='file'
									accept={mimeTypes.join(',')}
									multiple={props.isMultiple}
									hidden
									onChange={async (e) => {
										if (!e.target.files) return;
										setInternalLoading(true);

										const selectedFiles = Array.from(e.target.files);
										const fulfilled: File[] = [];
										const rejected: {
											error: Error;
											fileName: string;
											mimeType: string;
										}[] = [];

										selectedFiles.forEach((file) => {
											if (mimeTypes.includes(file.type)) {
												fulfilled.push(file);
											} else {
												rejected.push({
													error: new Error('Unsupported file type'),
													fileName: file.name,
													mimeType: file.type,
												});
											}
										});

										await handleFilesSelected(fulfilled, rejected);
										e.target.value = ''; // reset for re-select
									}}
								/>
								{props.placeholder?.title && (
									<div className='dropzone-title--rev'>
										{props.placeholder.title}
									</div>
								)}
								{props.placeholder?.description && (
									<div className='dropzone-description--rev'>
										{props.placeholder.description}
									</div>
								)}
							</>
						)}
					</div>
				</DropZoneAria>
			)}
		</>
	);
}
